/**
 * @fileoverview Application constants and configuration values.
 * @module constants
 */

// ============================================================================
// Application Metadata
// ============================================================================

export const APP_CONFIG = {
  name: 'ಕನ್ನಡ ತಿಪಿಟಕ',
  nameEnglish: 'Kannada Tipitaka',
  description: 'ಭಗವಾನ್ ಗೌತಮ ಬುದ್ಧರ ಮೂಲ ಬೋಧನೆಗಳು',
  descriptionEnglish: 'Original teachings of Lord Gautama Buddha',
  version: '1.0.0',
  repository: 'https://github.com/mahabodhi/kannada-tipitaka',
  organization: 'Buddha Vachana Trust',
  organizationUrl: 'https://mahabodhi.org',
} as const;

// ============================================================================
// Route Paths
// ============================================================================

export const ROUTES = {
  HOME: '/',
  BOOKS: '/books',
  CONTRIBUTORS: '/contributors',
  SUTTA: '/:suttaId',
} as const;

// ============================================================================
// Nikāya Configuration
// ============================================================================

export const NIKAYA_CONFIG = {
  dn: {
    abbreviation: 'dn',
    paliName: 'Dīgha Nikāya',
    kannadaName: 'ದೀಘನಿಕಾಯ',
    urlSegment: 'dīghanikāya',
    description: 'Long Discourses',
  },
  mn: {
    abbreviation: 'mn',
    paliName: 'Majjhima Nikāya',
    kannadaName: 'ಮಜ್ಝಿಮನಿಕಾಯ',
    urlSegment: 'majjhimanikāya',
    description: 'Middle Length Discourses',
  },
  sn: {
    abbreviation: 'sn',
    paliName: 'Saṃyutta Nikāya',
    kannadaName: 'ಸಂಯುತ್ತನಿಕಾಯ',
    urlSegment: 'saṃyuttanikāya',
    description: 'Connected Discourses',
  },
  an: {
    abbreviation: 'an',
    paliName: 'Aṅguttara Nikāya',
    kannadaName: 'ಅಙ್ಗುತ್ತರನಿಕಾಯ',
    urlSegment: 'aṅguttaranikāya',
    description: 'Numerical Discourses',
  },
} as const;

export type NikayaKey = keyof typeof NIKAYA_CONFIG;

// ============================================================================
// Theme Configuration
// ============================================================================

export const THEME_CONFIG = {
  light: {
    id: 'light',
    label: 'ಬೆಳಕು',
    labelEnglish: 'Light',
    icon: 'bi-sun-fill',
  },
  dark: {
    id: 'dark',
    label: 'ಕತ್ತಲೆ',
    labelEnglish: 'Dark',
    icon: 'bi-moon-stars-fill',
  },
  sepia: {
    id: 'sepia',
    label: 'ಸೀಪಿಯಾ',
    labelEnglish: 'Sepia',
    icon: 'bi-book-fill',
  },
} as const;

// ============================================================================
// Typography Configuration
// ============================================================================

export const TYPOGRAPHY = {
  fontSize: {
    min: 14,
    max: 24,
    default: 16,
    step: 1,
  },
  fontFamily: {
    kannada: "'Tiro Kannada', 'Noto Sans Kannada', serif",
    sans: "'Inter', system-ui, -apple-system, sans-serif",
    serif: "'Tiro Kannada', Georgia, serif",
  },
} as const;

// ============================================================================
// Local Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  THEME: 'tipitaka_theme',
  FONT_SIZE: 'tipitaka_font_size',
  LANGUAGE: 'tipitaka_language',
  READING_PROGRESS: 'tipitaka_reading_progress',
  BOOKMARKS: 'tipitaka_bookmarks',
} as const;

// ============================================================================
// Search Configuration
// ============================================================================

export const SEARCH_CONFIG = {
  minQueryLength: 2,
  maxResults: 10,
  debounceMs: 300,
  highlightTag: 'mark',
} as const;

// ============================================================================
// Reader Configuration
// ============================================================================

export const READER_TABS = {
  pali: {
    id: 0,
    label: 'ಪಾಳಿ',
    labelEnglish: 'Pāḷi',
  },
  kannada: {
    id: 1,
    label: 'ಕನ್ನಡ',
    labelEnglish: 'Kannada',
  },
  both: {
    id: 2,
    label: 'ಪಾಳಿ-ಕನ್ನಡ',
    labelEnglish: 'Pāḷi-Kannada',
  },
  summary: {
    id: 3,
    label: 'ಸಾರಾಂಶ',
    labelEnglish: 'Summary',
  },
  notes: {
    id: 4,
    label: 'ಟಿಪ್ಪಣಿಗಳು',
    labelEnglish: 'Notes',
  },
} as const;

// ============================================================================
// External Links
// ============================================================================

export const EXTERNAL_LINKS = {
  suttaCentral: 'https://suttacentral.net',
  mahaBodhi: 'https://mahabodhi.org',
  tipitakaOrg: 'https://tipitaka.org',
  vri: 'https://www.vridhamma.org',
} as const;

// ============================================================================
// Breakpoints (matching Bootstrap)
// ============================================================================

export const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;
